import { Badge, Flex, Text } from '@radix-ui/themes';
import React, { useEffect, useState, useCallback } from 'react';
import TableComponent from '../components/ui-components/TableComponent';
import ViewGateway from '../components/ui-components/ViewGateway';
import SearchFilter from '../components/ui-components/SearchFilter';
import PaginationComponent from '../components/ui-components/PaginationComponent';
import AddGatewayPopup from '../components/ui-components/AddGatewayPopup';
import { MdOutlineVerifiedUser } from 'react-icons/md';
import axiosInstance from '../config/axiosConfig';
import debounce from 'lodash/debounce';
import { highlightSearchTerm } from '../utils/helpers';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const GatewayAdminPage = () => {
  const [gateways, setGateways] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const pageSize = 10;

  const showToast = (message, type = 'success') => {
    if (type === 'error') toast.error(message);
    else toast.success(message);
  };

  const fetchGateways = useCallback(async (page = 1, query = searchQuery, activeOnly = showActiveOnly) => {
    try {
      const response = await axiosInstance.get('/gateway/get-institution-gateways', {
        params: {
          pageNumber: page,
          pageSize,
          searchQuery: query.length >= 3 ? query : null,
          isActive: activeOnly ? true : null,
        },
      });
      if (response.data && response.data.result) {
        const gatewayData = response.data.result.gatewayList.map((item, index) => ({
          serialNumber: (page - 1) * pageSize + index + 1,
          token: item.token,
          igin: item.igin,
          igvn: item.igvn,
          isActive: item.isActive,
          // gateway_address intentionally omitted for admin
        }));
        setGateways(gatewayData);
        setTotalCount(response.data.result.totalCount || 0);
      } else {
        setGateways([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching gateways:', error.response ? error.response.data : error.message);
      setGateways([]);
      setTotalCount(0);
    }
  }, [searchQuery, showActiveOnly]);

  const debouncedFetch = useCallback(debounce((page, query, activeOnly) => {
    fetchGateways(page, query, activeOnly);
  }, 300), [fetchGateways]);

  useEffect(() => {
    debouncedFetch(currentPage, searchQuery, showActiveOnly);
    return () => debouncedFetch.cancel();
  }, [currentPage, searchQuery, showActiveOnly, debouncedFetch]);

  const handleSearchChange = () => { setCurrentPage(1); };
  const handleActiveToggle = () => { setCurrentPage(1); };
  const handleGatewayUpdated = async (change = null) => {
    if (change?.deleted) {
      setGateways((items) => items.filter((item) => item.token !== change.token));
      setTotalCount((count) => Math.max(0, count - 1));
    } else if (typeof change?.isActive === 'boolean') {
      setGateways((items) =>
        items.map((item) =>
          item.token === change.token ? { ...item, isActive: change.isActive } : item
        )
      );
    }
    await fetchGateways(currentPage, searchQuery, showActiveOnly);
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const pageStartIndex = (currentPage - 1) * pageSize;

  const columns = [
    {
      title: '#',
      key: 'serialNumber',
      render: (value) => <Text size={2}>{value}</Text>,
    },
    {
      title: 'IGIN',
      key: 'igin',
      render: (value) => <Text size={2}>{highlightSearchTerm(value, searchQuery)}</Text>,
    },
    {
      title: 'IGVN',
      key: 'igvn',
      render: (value) => <Text size={2}>{highlightSearchTerm(value, searchQuery)}</Text>,
    },
    {
      title: 'Status',
      key: 'isActive',
      render: (value) => (
        <Badge color={value ? 'green' : 'tomato'}>
          <MdOutlineVerifiedUser color={value ? 'green' : 'red'} />
          {value ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, row) => (
        <Flex gap={'2'} align={'center'}>
          <ViewGateway
            initialValues={row}
            onGatewayUpdated={handleGatewayUpdated}
            showToast={showToast}
            isAdmin={true}
          />
        </Flex>
      ),
    },
  ];

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <Flex direction="column" gap="2">
        <Flex justify="between" align="center" mb="4">
          <SearchFilter
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            showActiveOnly={showActiveOnly}
            setShowActiveOnly={setShowActiveOnly}
            placeholder="Search gateways (min 3 chars)"
            onSearchChange={handleSearchChange}
            onActiveToggle={handleActiveToggle}
          />
          <AddGatewayPopup
            onGatewayAssigned={() => fetchGateways(1, searchQuery, showActiveOnly)}
            showToast={showToast}
          />
        </Flex>
        <TableComponent dataStructure={gateways} columns={columns} />
        {totalCount > 0 && (
          <PaginationComponent
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(newPage) => setCurrentPage(newPage)}
          />
        )}
        {totalCount > 0 && (
          <Text size="2" align="center" mt="2">
            Showing {pageStartIndex + 1} – {Math.min(pageStartIndex + pageSize, totalCount)} of {totalCount} gateways
          </Text>
        )}
        {totalCount === 0 && !gateways.length && (
          <Flex justify="center" align="center" style={{ minHeight: '120px' }}>
            <Text size="3" color="gray">No gateways found</Text>
          </Flex>
        )}
      </Flex>
    </>
  );
};

export default GatewayAdminPage;
